import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Divider, Tabs } from "antd";
import Title from "antd/es/typography/Title";
import { toast } from "utils/toast";

import { useUpdateOrganization } from "hooks/useOrganizations";
import usePageLoading from "hooks/usePageLoading";

import {
  FormSkeleton,
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SlowStateBanner,
} from "js/components/Loading";
import BuroDeCredito from "js/components/Organization/Configurations/BuroDeCredito";
import General from "js/components/Organization/Configurations/General";
import { GeneralOrganizationData } from "js/components/Organization/types";
import { Button } from "js/components/ui/button";

import { organizationDataUpdated } from "../../../store/features/organization/organization";
import { loadOrganizationData } from "../../../store/features/organization/organizationActions";

const Configuration: React.FC = () => {
  const { organizationData, loading } = useSelector(
    (state: any) => state.entities.organization,
  );
  const dispatch = useDispatch();
  const updateMutation = useUpdateOrganization();

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: loading && !organizationData,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    dispatch(loadOrganizationData());
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-9 w-96" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-6">
      <SkeletonBlock className="h-px w-full bg-slate-200" />
      <div className="flex gap-6">
        <div className="flex w-32 flex-col gap-2">
          <SkeletonBlock className="h-10 w-full rounded-lg" />
          <SkeletonBlock className="h-10 w-full rounded-lg" />
        </div>
        <div className="flex-1">
          <FormSkeleton fieldCount={8} columns={2} />
        </div>
      </div>
    </div>
  );

  const onFinish = async (values: GeneralOrganizationData) => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        dispatch(
          organizationDataUpdated({
            ...organizationData,
            name: values.name,
            use_quotes: values.use_quotes,
            visualize_members: values.visualize_members,
            visualize_quotes: values.visualize_quotes,
            send_multireport_end_user: values.send_multireport_end_user,
          }),
        );
      },
    });
  };

  const itemTabs = [
    {
      label: "General",
      key: "1",
      children: (
        <General
          data={organizationData}
          onFinish={onFinish}
          loading={updateMutation.isPending}
        />
      ),
    },
    {
      label: "Buró de Crédito",
      key: "2",
      children: (
        <BuroDeCredito
          data={organizationData}
          onFinish={onFinish}
          loading={updateMutation.isPending}
        />
      ),
      disabled: true,
      // disabled: !organizationData?.buro_de_credito_use_custom_credentials,
    },
  ];

  return (
    <>
      <PageLoadingContainer
        showSkeleton={showSkeleton}
        slowState={slowStateBanner}
        headerSkeleton={headerSkeleton}
        contentSkeleton={contentSkeleton}
        className="space-y-6"
      >
        <div className="space-y-6">
          <div>
            <Title level={2}>Configuración de la Organización</Title>
          </div>
          <Divider />
          <div style={{ marginTop: 30 }}>
            <Tabs defaultActiveKey="1" tabPosition={"left"} items={itemTabs} />
          </div>
        </div>
      </PageLoadingContainer>
    </>
  );
};
export default Configuration;
